// SuscripcionGate.js
//
// Dos superficies del mismo estado de acceso (Planner #862, Entrega 2), un
// solo componente porque design.md no lista un archivo aparte para la
// segunda:
//   - R30/R33: pantalla de bloqueo por pago vencido o prueba vencida.
//     Mockup elegido: bloqueo D, "pantalla completa con pasos" — sin tarjeta
//     ni fondo oscuro, la app entera pasa a ser dos columnas: a la izquierda
//     qué pasó, a la derecha cómo se destraba en tres pasos numerados.
//   - R31: aviso NO bloqueante mientras la cuenta está en gracia. El spec
//     aprobado no trae una variante de mockup para esto (las tres elegidas —
//     bloqueo D, perfil B, admin B— son para el bloqueo total, el panel del
//     perfil y el cajón de admin); el `.banner.warn` del mockup vive DENTRO
//     del panel de perfil (ya construido en la Entrega 1,
//     `ProfileView.js::SuscripcionPanel`), no como pieza global. Para que el
//     aviso valga en CUALQUIER módulo (Turno incluido) sin tocar un solo
//     archivo fuera de esta lista —ni `Turno.js` ni el navbar están en la
//     tabla de design.md—, se resuelve como una tarjeta flotante fija en la
//     esquina, con el mismo lenguaje visual (ícono + título + descripción,
//     tokens de warning) y position `fixed` acotada a su propio tamaño: no
//     hay overlay ni bloqueo de scroll, así que todo lo demás —incluido el
//     cockpit del Turno— sigue operable debajo y alrededor.
//
// Se dibuja SOLO si `GET /api/user/me/` trae la clave `access` (R33: en la
// ventana de transición del deploy —frontend nuevo contra backend viejo— esa
// clave puede no existir todavía, y sin ella no hay nada que evaluar: ni
// gate ni banner). El bloqueo también se levanta a mitad de sesión (R32)
// cuando cualquier llamada de la app recibe un 403 con un `code` de pago
// (`get-client.js` emite `smartdx:acceso-bloqueado`), sin esperar a que el
// próximo `/me/` corra ni recargar la página.
//
// El bloqueo sigue el patrón de `TosAcceptanceGate.js` (overlay `fixed` +
// bloqueo de scroll del body mientras está montado): taparlo TODO —ningún
// módulo detrás queda operable— es la exigencia de R30, no un detalle
// visual. El banner de gracia NUNCA toca `document.body.style.overflow`.
import { useEffect, useState } from "react";
import { Box, Button, IconButton, Link, Typography } from "@mui/material";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { useCurrentUser } from "../context/UserContext";
import { montoBrutoSugerido } from "../../helpers/billing-monto";
import { formatoClp } from "../../helpers/checkout-formato";
import PlanesYPago from "../checkout/PlanesYPago";
import CheckoutDialog from "../checkout/CheckoutDialog";
import { botonGhostSx, botonPrimarioSx, botonTextoSx } from "../checkout/botonesSx";
import useCedeAnteCapas from "../../hooks/useCedeAnteCapas";
import AvisoPagoDialog, { BotonWhatsApp, faltaPara } from "../checkout/AvisoPagoDialog";
import { pedirTurno } from "../../helpers/aviso-pago-api";
import { hayGateDeAltaActivo } from "../../helpers/turno-lugares-gate";
import useAhora from "../../hooks/useAhora";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { WHATSAPP_SOPORTE } from "../../helpers/soporte";
import { esInterno, profesionDeCuenta } from "../../helpers/profesiones";
import { logActivity } from "../../helpers/activity-log";

// Solo estos dos codes tapan la app entera: son los que declara
// `billing.access.access_state` para "el acceso está negado" (R15-R20). Otros
// 403 del backend (módulo no habilitado, ToS sin aceptar) tienen su propio
// tratamiento y no pasan por acá.
// #1809: `cobro_programado_vencido` es el corte por cuenta que activa Sebastián (pasó el día de
// pago sin cobro). Usa esta misma pantalla, con su texto y «Estoy de turno».
const CODES_QUE_BLOQUEAN = new Set(["trial_expired", "subscription_past_due", "cobro_programado_vencido"]);

// #1809 (revisor v10, O1): el link del admin es `/perfil?pagar=1`. Sin sesión abierta, el login redirige y
// el parámetro se pierde; por eso se anota apenas carga el bundle (antes de cualquier ruta) en
// sessionStorage, que sobrevive al login en la misma pestaña, y el gate lo consume una vez.
const MARCA_PAGAR = "smartdx_abrir_aviso_pago";
try {
  if (new URLSearchParams(window.location.search).get("pagar") === "1") {
    window.sessionStorage.setItem(MARCA_PAGAR, "1");
  }
} catch {
  /* sin URL o sin sessionStorage: el aviso sale igual en su fecha */
}

// Datos bancarios REALES de Salud Wild SpA (#1680, R32): son de la EMPRESA, no
// de la cuenta del médico, así que van estáticos. Los de antes (RUT 77.845.320-1
// y "Mercado Pago · vista 1029384756") eran placeholders del mockup viejo.
const DATOS_BANCARIOS = [
  { etiqueta: "Titular", valor: "Salud Wild SpA", copiable: false },
  { etiqueta: "RUT", valor: "78.149.483-6", copiable: true },
  { etiqueta: "Banco", valor: "Itaú", copiable: false },
  { etiqueta: "Cuenta corriente", valor: "0232106231", copiable: true },
  { etiqueta: "Comprobante a", valor: "pagos@smardx.com", copiable: true },
];

// I-1 (revisor r3): el monto de la caja de transferencia en CLP. Con la lista de planes a la vista
// (incluida una `cancelada` vencida que elige otro plan) sale del plan ELEGIDO; solo en la rama
// "Tu plan" (suscripción activa vencida, sin lista) sale de la suscripción (`precio_bruto`).
const montoTransferencia = (estadoCheckout, ctx) => {
  if (ctx && ctx.planElegido) {
    return ctx.moneda === "CLP" && ctx.precio != null ? formatoClp(ctx.precio) : "";
  }
  return estadoCheckout?.suscripcion?.moneda === "CLP" ? formatoClp(estadoCheckout.suscripcion.precio_bruto) : "";
};

const formatoMoneda = (monto, moneda) => {
  if (monto === null || monto === undefined || monto === "" || !Number.isFinite(Number(monto))) return "";
  const cifra = Number(monto).toLocaleString("es-CL", { maximumFractionDigits: 2 });
  return moneda === "USD" ? `USD ${cifra}` : `$${cifra}`;
};

const formatoFechaLarga = (iso) => {
  if (!iso) return "";
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString("es-CL", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const copiar = (texto) => {
  try {
    navigator.clipboard?.writeText(texto);
  } catch {
    /* portapapeles no disponible: nunca bloqueante */
  }
};

const FilaBanco = ({ etiqueta, valor, copiable }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
    <Typography sx={{ fontSize: 11.5, color: "var(--text-muted)" }}>{etiqueta}</Typography>
    <Typography
      sx={{ fontSize: 12.5, fontWeight: 600, display: "flex", alignItems: "center", gap: 0.5 }}
    >
      {valor}
      {copiable && (
        <Box
          component="button"
          type="button"
          onClick={() => copiar(valor)}
          aria-label={`Copiar ${etiqueta}`}
          sx={{
            display: "inline-flex",
            border: "none",
            background: "transparent",
            cursor: "pointer",
            color: "var(--text-muted)",
            p: 0.25,
          }}
        >
          <ContentCopyOutlinedIcon sx={{ fontSize: 14 }} />
        </Box>
      )}
    </Typography>
  </Box>
);

// El diálogo de pago de la tarjeta de prueba vive en este envoltorio y no dentro
// de la tarjeta: al confirmarse el pago `/me/` cambia, la tarjeta de la prueba
// desaparece y, si el diálogo colgara de ella, se desmontaría antes de mostrar
// "Listo" (#1680, R31).
const SuscripcionGate = (props) => {
  const [checkoutAbierto, setCheckoutAbierto] = useState(false);
  // #1809: el popup del cobro programado. La fase y si se muestra los decide el backend
  // (`aviso_pago.mostrar`); "Ahora no" lo cierra en esta sesión y el backend no lo vuelve a
  // mandar hasta mañana (fase aviso). En el día de pago vuelve en cada ingreso.
  const { currentUser, loading } = useCurrentUser();
  const aviso = currentUser?.aviso_pago || null;
  const [avisoCerrado, setAvisoCerrado] = useState(false);
  const [avisoForzado, setAvisoForzado] = useState(false);
  // Revisor #1809 v9: el link que Sebastián copia en el admin es `/perfil?pagar=1`. Abre el aviso aunque
  // el backend todavía no lo muestre (fase `programado`, o «Ahora no» de hoy): si no, el médico entraba,
  // veía «Al día» y no tenía dónde pagar. Se consume una vez y se saca de la URL.
  const fasePagable = ["programado", "aviso", "dia_de_pago"].includes(aviso?.fase);
  useEffect(() => {
    if (!fasePagable) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const enUrl = params.get("pagar") === "1";
      let marcado = false;
      try {
        marcado = window.sessionStorage.getItem(MARCA_PAGAR) === "1";
      } catch {
        /* storage bloqueado: vale solo la URL */
      }
      if (!enUrl && !marcado) return;
      setAvisoForzado(true);
      // La URL se limpia ANTES que el sessionStorage (revisor v11, P3): con el storage bloqueado, el
      // removeItem tira y el `?pagar=1` quedaba en la URL, reabriendo el aviso en cada recarga.
      if (enUrl) {
        params.delete("pagar");
        const resto = params.toString();
        window.history.replaceState(null, "", `${window.location.pathname}${resto ? `?${resto}` : ""}${window.location.hash}`);
      }
      window.sessionStorage.removeItem(MARCA_PAGAR);
    } catch {
      /* URL no disponible: el aviso sale igual en su fecha */
    }
  }, [fasePagable]);
  // Revisor #1809 v1 (falla 1): con un gate del alta encima (celular, Términos, perfil) el popup
  // se abría DEBAJO y le robaba el teclado al campo del celular (mismo patrón que INC-2026-52):
  // el médico quedaba atrapado. Con cualquier gate del alta activo, el aviso espera.
  const bloqueado = hayGateDeAltaActivo(currentUser);
  const avisoAbierto =
    !loading &&
    !props.enOnboarding &&
    !bloqueado &&
    Boolean(aviso) &&
    (avisoForzado ||
      (aviso.mostrar && !avisoCerrado && ["aviso", "dia_de_pago", "pago_en_proceso"].includes(aviso.fase)));
  return (
    <>
      <SuscripcionGateContenido
        {...props}
        onElegirPlan={() => setCheckoutAbierto(true)}
        onAbrirAviso={() => setAvisoForzado(true)}
      />
      <CheckoutDialog open={checkoutAbierto} onClose={() => setCheckoutAbierto(false)} />
      <AvisoPagoDialog
        aviso={aviso}
        open={avisoAbierto}
        onCerrar={() => {
          setAvisoCerrado(true);
          setAvisoForzado(false);
        }}
      />
    </>
  );
};

const SuscripcionGateContenido = ({ enOnboarding = false, onElegirPlan, onAbrirAviso }) => {
  const { currentUser, loading, refreshUser } = useCurrentUser();
  const [pidiendoHora, setPidiendoHora] = useState(false);
  // Revisor #1809 v1: la cuenta regresiva de «Estoy de turno» quedaba congelada en el primer render.
  const ahoraTurno = useAhora(currentUser?.aviso_pago?.fase === "turno_extra");
  const [errorHora, setErrorHora] = useState("");
  const [codeDeEvento, setCodeDeEvento] = useState(null);
  const [revisando, setRevisando] = useState(false);
  const [noEncontrado, setNoEncontrado] = useState(false);
  const [bannerCerrado, setBannerCerrado] = useState(false);
  // #1680: mientras hay un pago en curso (confirmando / listo / pendiente) la
  // pantalla de bloqueo se retiene con el code que tenía, aunque /me/ ya diga que
  // el acceso está permitido: el médico tiene que ver el resultado y seguir él.
  const [codeRetenido, setCodeRetenido] = useState(null);
  // Fase del bloque de pago: con la transferencia avisada ("pendiente") ese bloque trae su propio
  // botón de "volver a revisar" y esta pantalla no suma un segundo.
  const [faseCheckout, setFaseCheckout] = useState("planes");
  const [transferenciaAbierta, setTransferenciaAbierta] = useState(false);
  // Estado propio para el banner de demo (R20, #1133): aunque "gracia" y
  // "demo" son mutuamente excluyentes en el backend (nunca se muestran
  // juntos), cada tarjeta se cierra sola, sin compartir el booleano.
  //
  // R35 (v2): el cierre se persiste en `sessionStorage`, con clave por
  // usuario (mismo patrón de scoping por dueño que exige
  // `turno_draft_buffer_cruce_phi`, para que el cierre de un médico no se
  // filtre a otro en el mismo navegador). `useState` puro se reseteaba en
  // cada mount: antes de este fix, cada recarga volvía a mostrar la
  // tarjeta, durante los 14 días completos de todo usuario nuevo.
  const [bannerDemoCerrado, setBannerDemoCerradoState] = useState(false);
  const demoUserId = currentUser?.user_id;
  useEffect(() => {
    if (!demoUserId) return;
    try {
      const cerrado = window.sessionStorage.getItem(`smartdx_demo_banner_cerrado_${demoUserId}`) === "1";
      setBannerDemoCerradoState(cerrado);
    } catch {
      /* sessionStorage no disponible (modo privado, etc.): nunca bloqueante */
    }
  }, [demoUserId]);
  const cerrarBannerDemo = () => {
    setBannerDemoCerradoState(true);
    if (!demoUserId) return;
    try {
      window.sessionStorage.setItem(`smartdx_demo_banner_cerrado_${demoUserId}`, "1");
    } catch {
      /* no bloqueante */
    }
  };

  // R32: el 403 puede llegar de cualquier endpoint gateado por
  // `AccessActive` mientras el médico ya está adentro (el `/me/` del
  // arranque decía que sí podía entrar, y venció en el medio). El evento lo
  // emite `get-client.js`, mismo patrón que el 429 del tope diario.
  useEffect(() => {
    const onBloqueo = (event) => {
      const code = event?.detail?.code;
      if (CODES_QUE_BLOQUEAN.has(code)) setCodeDeEvento(code);
    };
    window.addEventListener("smartdx:acceso-bloqueado", onBloqueo);
    return () => window.removeEventListener("smartdx:acceso-bloqueado", onBloqueo);
  }, []);

  // R33: sin `access` en la respuesta (contrato viejo) no hay nada que
  // evaluar. `tieneAccess` es la guarda: un evento de 403 nunca dibuja nada
  // por sí solo si `/me/` no llegó a confirmar la clave.
  const access = currentUser?.access;
  const tieneAccess = Boolean(access);
  const codeDesdeAccess = tieneAccess && access.permite === false ? access.code : null;
  // front-13 (revisión 28-sep): `codeDesdeAccess` viene del `/me` del
  // ARRANQUE (o del último refresh) y puede quedar viejo cuando, a mitad de
  // sesión, la demo vence de verdad y un 403 `trial_expired` llega por
  // `smartdx:acceso-bloqueado` (`codeDeEvento`). El evento en vivo es más
  // reciente que el `/me` cacheado, así que gana. #1529: `documents_required`
  // ya no se emite (se retiró el corte por documentos); el caso solo puede
  // venir de un `/me` de un backend viejo y sigue sin abrir este gate.
  const code = codeRetenido || codeDeEvento || codeDesdeAccess;
  const showGate = !loading && tieneAccess && CODES_QUE_BLOQUEAN.has(code);
  // Cubre R33 — .claude/specs/jornada-suelta/ (P25, 31-oct-2026): la bajada de la prueba terminada nombra las jornadas
  // adicionales solo si `PlanesYPago` informa que las ofrece (`onOfreceSuelta`: oferta + riel Flow + sin suscripción).
  // Mirar solo la oferta no alcanzaba: al cambiar el país a CO/PE la opción se oculta y la bajada seguía nombrándolas.
  // Mientras el checkout no haya cargado (cargando, o falló la primera carga) es false: texto de siempre, sin parpadeo.
  // Si una recarga posterior falla, PlanesYPago conserva el país anterior y este valor lo acompaña.
  const [ofreceSueltaReal, setOfreceSueltaReal] = useState(false);
  // R31: en gracia, `access.permite` es `true` y `access.aviso === "gracia"`
  // (ver `billing.access.access_state`). El gate SIEMPRE gana si por algún
  // motivo las dos condiciones se dieran juntas (no debería pasar: son
  // estados mutuamente excluyentes en el backend).
  const enGracia = tieneAccess && !showGate && access.estado === "gracia" && access.aviso === "gracia";
  const showBanner = !loading && enGracia && !bannerCerrado;
  // R20: mientras el estado de acceso sea "demo" (billing.access.access_state),
  // la misma tarjeta flotante no bloqueante, con los días restantes de la
  // prueba. Se muestra durante TODA la demo, no solo cerca del corte —
  // consistente con el banner de gracia, que hace lo mismo durante todo su
  // período (alternativa descartada en design.md: un umbral propio sería una
  // asimetría no pedida).
  // Cubre R26 — .claude/specs/1583-promo-octubre/: con la cuenta en pausa por
  // inactividad (`permite === false`, `estado` sigue "demo") esta tarjeta no se
  // dibuja: el overlay de PausaInactividadGate manda y nunca hay dos capas.
  const enDemo =
    tieneAccess && !showGate && access.estado === "demo" && access.permite !== false;
  // front-1/e2e-1 (revisión adversarial 28-sep, #1133 R20 + #1401 R24/R37):
  // hasta esta ronda, esta tarjeta se apagaba SIEMPRE que `alta_etapa ===
  // "sin_documentos"`, con el argumento de que el checklist "Primeros
  // pasos" (`AvisosPrimerosPasos.jsx`) YA mostraba el plazo. Eso dejó de ser
  // cierto (#1529: el paso de documentos ya no muestra ningún plazo, y antes
  // mostraba el de DOCUMENTOS, no la demo) — y la única pieza que sí muestra la demo
  // (`popup-estado-demo`) vive DENTRO del popup del chip, que ni siquiera
  // se monta si la cuenta ya tiene unidad y paciente cargados (el chip solo
  // aparece con `showDocs || showUnidad || recienConfigurada`). Resultado
  // medido: una cuenta sin documentos, con unidad y menos de 3 días de uso,
  // no veía SU demo en ningún lado. Vuelve a la regla original de R20 de
  // #1133 ("se muestra durante TODA la demo"), sin la excepción de
  // `alta_etapa`: la posible duplicación visual con el chip (cuando el chip
  // sí está visible) es un costo menor comparado con dejar al médico ciego
  // de su propio plazo.
  // #1525 (revisor, F-1): dentro del onboarding la tarjeta tapaba a 390 px el
  // control de abajo de cada paso (Continuar, Saltar por ahora, Completar
  // después) y la única salida era su ×. Ahí no va: el paso Listo ya cuenta los
  // 14 días, y la tarjeta vuelve apenas sale del wizard. La ruta la pasa
  // `AppShell` (este componente se monta también fuera de un Router en tests).
  const showBannerDemo = !loading && enDemo && !bannerDemoCerrado && !enOnboarding;
  // H2 (revisor E2E del paquete final, #1401 v5 × #1450): la tarjeta que se
  // dibuje (gracia o demo) cede mientras una marca de acompañamiento de #1450
  // se le solaparía; a 390 la marca del paso 1 caía encima de "Cerrar aviso"
  // y se comía el toque. Ver `helpers/capasFlotantes.js`.
  const [tarjetaRef, tarjetaCede] = useCedeAnteCapas(showBanner || showBannerDemo);
  const cederSx = tarjetaCede ? { visibility: "hidden", pointerEvents: "none" } : null;

  // Bloquea el scroll del fondo SOLO mientras el overlay bloqueante está
  // montado (mismo patrón que TosAcceptanceGate.js). El banner de gracia
  // nunca toca esto: no es un overlay, es una tarjeta flotante acotada.
  useEffect(() => {
    if (!showGate) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showGate]);

  if (showBanner) {
    const dias = access.dias_restantes;
    const fecha = access.fecha_corte ? formatoFechaLarga(access.fecha_corte) : "";
    return (
      <Box
        ref={tarjetaRef}
        data-testid="suscripcion-banner-gracia"
        data-cede={tarjetaCede ? "1" : undefined}
        role="status"
        sx={{
          ...cederSx,
          position: "fixed",
          // Ronda 5 (revisor, F3): a la derecha, en 390, esa esquina es
          // donde vive fab-mic (y cualquier otro FAB futuro del Turno) — con
          // ancho pegado al borde derecho la tarjeta lo tapaba igual aunque
          // se moviera a la izquierda (el ancho llegaba hasta el borde de
          // todos modos). Ahora, en mobile, se achica para dejar el rincón
          // libre (56px del FAB + margen) en vez de solo reposicionarse.
          left: { xs: 12, sm: "auto" },
          right: { xs: "auto", sm: 20 },
          bottom: { xs: 12, sm: 20 },
          // front-1/R22 (revisor final v5, "prueba ad hoc" mobile 390): con
          // 15000 esta tarjeta quedaba POR ENCIMA de cualquier `Dialog` de
          // MUI (z-index del modal, 1300 por defecto) — incluidos los que
          // van `fullScreen` en mobile (ej. `PrimeraUnidadDialog.jsx`), así
          // que tapaba sus botones y les robaba el click aunque el diálogo
          // estuviera "abierto encima". Con `theme.zIndex.modal - 1` (1299)
          // quedaba TODAVÍA por encima de un `Drawer` de MUI (z-index 1200
          // por defecto: `sheet-acciones`/`TurnoCockpitMobileB.jsx` y el
          // buscador de pestañas son `Drawer`, no `Dialog`) — revisor final
          // v5 vuelta 2, "falla B": a 390 tapaba "Paciente extra",
          // "Actualizar camas" y "Crear pestaña" dentro del sheet abierto, y
          // una fila de paciente dentro del buscador. `theme.zIndex.drawer -
          // 1` (1199) la deja flotando sobre el contenido normal de la
          // página (lo que necesita, para no perder R20/#1133) y SIEMPRE por
          // debajo de cualquier `Drawer` o `Dialog` de MUI (que arrancan en
          // 1200/1300 por defecto). Revisor final v5 vuelta 3, hallazgo 2:
          // esto NO cubre "cualquier elemento presente o futuro" — un FAB
          // (como `fab-mic`, que va en 1300) u otro elemento con un z-index
          // MENOR a 1199 en esta misma esquina SÍ quedaría tapado por esta
          // tarjeta; a la fecha ningún FAB del Turno cae ahí (`fab-mic` está
          // en 1300, por encima).
          zIndex: (theme) => theme.zIndex.drawer - 1,
          width: { xs: "calc(100vw - 24px - 70px)", sm: 360 },
          maxWidth: { xs: "calc(100vw - 24px - 70px)", sm: 360 },
          display: "flex",
          gap: 1.1,
          alignItems: "flex-start",
          p: 1.5,
          borderRadius: "14px",
          backgroundColor: "var(--bg-elevated)",
          border: "1px solid rgba(var(--color-warning-rgb), 0.35)",
          boxShadow: "0 12px 32px rgba(var(--shadow-rgb), 0.28)",
        }}
      >
        <AccessTimeOutlinedIcon sx={{ fontSize: 20, color: "var(--color-warning)", mt: 0.2 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "var(--text-primary)" }}>
            {fecha ? `Tu plan está en gracia hasta el ${fecha}` : "Tu plan está en gracia"}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "var(--text-secondary)", mt: 0.25 }}>
            {typeof dias === "number"
              ? `Te quedan ${dias} día${dias === 1 ? "" : "s"} para regularizar el pago antes de que se suspenda el acceso.`
              : "Regulariza el pago antes de que se suspenda el acceso."}
          </Typography>
        </Box>
        <IconButton
          aria-label="Cerrar aviso"
          size="small"
          onClick={() => setBannerCerrado(true)}
          sx={{ color: "var(--text-muted)", mt: -0.5, mr: -0.5 }}
        >
          <CloseOutlinedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    );
  }

  if (showBannerDemo) {
    const dias = access.dias_restantes;
    const fecha = access.fecha_corte ? formatoFechaLarga(access.fecha_corte) : "";
    return (
      <Box
        ref={tarjetaRef}
        data-testid="demo-banner-vencimiento"
        data-cede={tarjetaCede ? "1" : undefined}
        role="status"
        sx={{
          ...cederSx,
          position: "fixed",
          right: { xs: 12, sm: 20 },
          // R34 (v2): en mobile, `fab-mic` (el botón de dictado del cockpit
          // del Turno) vive en la MISMA esquina, `bottom: max(18px, safe-area
          // + 14px)`, alto 56px — o sea ocupa la franja de 18 a 74px desde
          // abajo. La tarjeta sube por ENCIMA de esa franja (92px deja un
          // margen de ~18px libres) en vez de competir por z-index: el FAB
          // sigue tapable incluso si algún día cambia su z-index.
          bottom: { xs: 92, sm: 20 },
          // front-1/R22 + revisor final v5 vuelta 2 "falla B": ver el
          // comentario largo en el banner de gracia, arriba en este mismo
          // archivo — mismo motivo, misma solución (`theme.zIndex.drawer -
          // 1` en vez de 15000/`modal - 1`, para no tapar ni diálogos
          // modales como `PrimeraUnidadDialog.jsx` NI Drawers de MUI como
          // `sheet-acciones`).
          zIndex: (theme) => theme.zIndex.drawer - 1,
          width: { xs: "calc(100vw - 24px)", sm: 360 },
          maxWidth: "calc(100vw - 24px)",
          display: "flex",
          gap: 1.1,
          alignItems: "flex-start",
          p: 1.5,
          borderRadius: "14px",
          backgroundColor: "var(--bg-elevated)",
          border: "1px solid rgba(var(--color-info-rgb), 0.35)",
          boxShadow: "0 12px 32px rgba(var(--shadow-rgb), 0.28)",
        }}
      >
        <AccessTimeOutlinedIcon sx={{ fontSize: 20, color: "var(--color-info)", mt: 0.2 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "var(--text-primary)" }}>
            Estás en tu prueba gratuita
          </Typography>
          <Typography sx={{ fontSize: 12, color: "var(--text-secondary)", mt: 0.25 }}>
            {typeof dias === "number"
              ? `Te quedan ${dias} día${dias === 1 ? "" : "s"}${fecha ? ` · vence el ${fecha}` : ""}.`
              : fecha
                ? `Tu prueba vence el ${fecha}.`
                : "Tu prueba gratuita sigue activa."}
          </Typography>
          <Button
            data-testid="checkout-abrir"
            size="small"
            onClick={onElegirPlan}
            sx={{ ...botonTextoSx, mt: 0.75, ml: -1, fontSize: 12.5 }}
          >
            Elegir plan
          </Button>
        </Box>
        <IconButton
          aria-label="Cerrar aviso"
          size="small"
          onClick={cerrarBannerDemo}
          sx={{ color: "var(--text-muted)", mt: -0.5, mr: -0.5 }}
        >
          <CloseOutlinedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    );
  }

  // #1809: con «Estoy de turno» la app sigue abierta hasta las próximas 9:00; esta franja lo dice y
  // lleva al mismo popup de pago. Misma tarjeta flotante que la de gracia (no bloquea nada).
  const avisoPago = currentUser?.aviso_pago;
  if (!showGate && !loading && avisoPago?.fase === "turno_extra") {
    const falta = faltaPara(avisoPago.extension_hasta, ahoraTurno);
    return (
      <Box
        ref={tarjetaRef}
        data-testid="aviso-turno-extra"
        role="status"
        sx={{
          position: "fixed",
          right: { xs: 12, sm: 20 },
          bottom: { xs: 92, sm: 20 },
          zIndex: (theme) => theme.zIndex.drawer - 1,
          width: { xs: "calc(100vw - 24px)", sm: 360 },
          maxWidth: "calc(100vw - 24px)",
          display: "flex",
          gap: 1.1,
          alignItems: "flex-start",
          p: 1.5,
          borderRadius: "14px",
          backgroundColor: "var(--bg-elevated)",
          border: "1px solid rgba(var(--color-warning-rgb), 0.35)",
          boxShadow: "0 12px 32px rgba(var(--shadow-rgb), 0.28)",
        }}
      >
        <AccessTimeOutlinedIcon sx={{ fontSize: 20, color: "var(--color-warning)", mt: 0.2 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "var(--text-primary)" }}>
            Sigues de turno hasta las 9:00
          </Typography>
          <Typography sx={{ fontSize: 12, color: "var(--text-secondary)", mt: 0.25 }}>
            {falta ? `Quedan ${falta}. ` : ""}Registra tu tarjeta para que el acceso no se vuelva a pausar.
          </Typography>
          <Button size="small" data-testid="aviso-turno-extra-pagar" onClick={onAbrirAviso} sx={{ ...botonTextoSx, mt: 0.75, ml: -1, fontSize: 12.5 }}>
            Registrar tarjeta
          </Button>
        </Box>
      </Box>
    );
  }

  if (!showGate) return null;

  // #1809: corte del cobro programado. Texto personalizado del admin si lo hay.
  const esCobroProgramado = code === "cobro_programado_vencido";
  const esPagoVencido = code === "subscription_past_due";
  // Prueba vencida de un MÉDICO: la salida principal es escribir por WhatsApp (el interno queda como estaba).
  // #1809: `profession` queda vacío hasta que suben documentos (opcionales desde #1529); un interno que solo
  // respondió «interno» en la bienvenida trae la `etapa`. `profesionDeCuenta` mira las dos (revisor v1, F1).
  const esPruebaMedico =
    !esCobroProgramado && !esPagoVencido && !esInterno(profesionDeCuenta(currentUser?.professional_profile));
  const urlContactoPrueba = `${WHATSAPP_SOPORTE}?text=${encodeURIComponent(
    "Hola Sebastián, se me terminó la prueba de SmartDx y quiero seguir usándola."
  )}`;
  const suscripcion = currentUser?.suscripcion;
  const fechaCorte = access?.fecha_corte ? formatoFechaLarga(access.fecha_corte) : "";

  const montoBruto =
    esPagoVencido && suscripcion
      ? formatoMoneda(
          montoBrutoSugerido(suscripcion.price_net, suscripcion.iva_rate),
          suscripcion.currency
        )
      : "";
  // R32: la transferencia es solo para suscripciones en CLP (USD cobra Paddle).
  const ofreceTransferencia = esPagoVencido && suscripcion?.currency === "CLP";

  const titulo = esCobroProgramado
    ? avisoPago?.titulo || "Pasó tu día de pago"
    : esPagoVencido
      ? "Tu plan venció y la gracia terminó"
      : "Tu período de prueba gratuito terminó";
  const subtitulo = esCobroProgramado
    ? avisoPago?.mensaje ||
      "Registra tu tarjeta y vuelves a trabajar al tiro. Si estás de turno, sigue hasta las 9:00 y lo resuelves después."
    : esPagoVencido
    ? `${fechaCorte ? `La gracia se cumplió el ${fechaCorte}. ` : ""}Paga de nuevo con tarjeta${
        ofreceTransferencia ? " o por transferencia" : ""
      } y vuelves a trabajar.`
    : esPruebaMedico
      ? ofreceSueltaReal
        ? "Si quieres seguir usándola o probándola, escríbenos y nos ponemos en contacto. Si no, suscríbete a un plan o elige jornadas por separado."
        : "Si quieres seguir usándola o probándola, escríbenos y nos ponemos en contacto. Si no, suscríbete a un plan."
      : ofreceSueltaReal
        ? "Para seguir usando el Turno elige un plan o suma jornadas adicionales."
        : "Para seguir usando el Turno elige un plan.";

  const handleRevisar = async () => {
    setRevisando(true);
    setNoEncontrado(false);
    try {
      const data = await refreshUser();
      if (data?.access?.permite) {
        setCodeDeEvento(null);
      } else {
        setNoEncontrado(true);
      }
    } catch {
      setNoEncontrado(true);
    } finally {
      setRevisando(false);
    }
  };

  // H2 (revisor #862, Entrega 2): el label y el aviso de "no encontrado"
  // hablaban SIEMPRE de una transferencia, aunque la causa del bloqueo fuera
  // `trial_expired` (nadie transfirió nada en ese caso: la propia pantalla
  // solo ofrece "Escríbenos a soporte" como paso). Se condicionan los dos
  // por `esPagoVencido`, la misma variable que ya decide el resto del copy
  // de esta pantalla.
  const labelBotonRevisar = esPagoVencido
    ? revisando
      ? "Revisando tu pago..."
      : ofreceTransferencia
        ? "Ya transferí, volver a revisar"
        : "Ya actualicé, volver a revisar"
    : revisando
      ? "Revisando..."
      : "Ya me activaron, volver a revisar"; // #1809: sin «pagar» en botones (médico e interno).

  const mensajeNoEncontrado = esPagoVencido
    ? "Todavía no vemos tu pago. Si transferiste hace menos de una hora, dale unos minutos " +
      "y vuelve a apretar. Si ya pasó más, escríbenos y lo revisamos a mano."
    : "Todavía no vemos novedades en tu cuenta. " +
      (esPruebaMedico ? "Si ya hablaste" : "Si ya pagaste o ya hablaste") +
      " con nosotros hace menos de una hora, " +
      "dale unos minutos y vuelve a apretar. Si ya pasó más, escríbenos a sebastian@smardx.com.";

  const botonRevisar = (
    <Box sx={{ mt: 1.75 }}>
      {noEncontrado && (
        <Box
          sx={{
            display: "flex",
            gap: 1.1,
            alignItems: "flex-start",
            p: 1.25,
            borderRadius: 2,
            mb: 1,
            backgroundColor: "rgba(var(--color-danger-rgb), 0.08)",
            border: "1px solid rgba(var(--color-danger-rgb), 0.3)",
          }}
        >
          <ErrorOutlineOutlinedIcon sx={{ fontSize: 18, color: "var(--color-danger)", mt: 0.1 }} />
          <Typography sx={{ fontSize: 12, color: "var(--text-secondary)" }}>
            {mensajeNoEncontrado}
          </Typography>
        </Box>
      )}
      <Button
        data-testid="suscripcion-gate-revisar"
        onClick={handleRevisar}
        disabled={revisando}
        variant="outlined"
        fullWidth
        startIcon={<RefreshOutlinedIcon sx={{ fontSize: 18 }} />}
        sx={botonGhostSx}
      >
        {labelBotonRevisar}
      </Button>
    </Box>
  );

  return (
    <Box
      data-testid="suscripcion-gate"
      role="dialog"
      aria-modal="true"
      aria-label="Acceso en pausa"
      sx={{
        position: "fixed",
        inset: 0,
        // Pantalla completa propia (no es un Dialog del tema): suma la barra de estado del iPhone.
        pt: "env(safe-area-inset-top)",
        boxSizing: "border-box",
        zIndex: 20000,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        backgroundColor: "var(--bg-elevated)",
        overflowY: "auto",
      }}
    >
      {/* Columna izquierda: qué pasó */}
      <Box
        sx={{
          p: { xs: 3, sm: 5, md: 7 },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 2.25,
          borderRight: { md: "1px solid rgba(var(--fg-rgb), 0.08)" },
        }}
      >
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: "12px",
            display: "grid",
            placeItems: "center",
            color: "var(--color-warning)",
            backgroundColor: "rgba(var(--color-warning-rgb), 0.14)",
            border: "1px solid rgba(var(--color-warning-rgb), 0.35)",
          }}
        >
          <AccessTimeOutlinedIcon sx={{ fontSize: 24 }} />
        </Box>
        <Box>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              color: "var(--text-muted)",
            }}
          >
            Modo Turno · acceso en pausa
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 20, sm: 27 },
              fontWeight: 800,
              mt: 0.5,
              maxWidth: "15ch",
              color: "var(--text-primary)",
            }}
          >
            {titulo}
          </Typography>
          <Typography sx={{ fontSize: { xs: 12.5, sm: 14 }, mt: 1, color: "var(--text-secondary)" }}>
            {subtitulo}
          </Typography>
        </Box>
        {esPruebaMedico && (
          <Box sx={{ maxWidth: 420 }}>
            <Button
              data-testid="gate-contacto-prueba"
              component="a"
              href={urlContactoPrueba}
              target="_blank"
              rel="noopener noreferrer"
              fullWidth
              size="large"
              variant="contained"
              startIcon={<WhatsAppIcon sx={{ fontSize: 20 }} />}
              onClick={() => logActivity("prueba_vencida_contacto_whatsapp", { module: "turno" })}
              sx={{ ...botonPrimarioSx, py: 1.25, textAlign: "center" }}
            >
              Quiero seguir usándola o probándola
            </Button>
            <Typography sx={{ fontSize: 12, mt: 0.75, color: "var(--text-muted)" }}>
              Te respondemos por WhatsApp.
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            gap: 1.25,
            alignItems: "flex-start",
            p: 1.5,
            borderRadius: 2,
            backgroundColor: "rgba(var(--color-success-rgb), 0.08)",
            border: "1px solid rgba(var(--color-success-rgb), 0.25)",
            maxWidth: 420,
          }}
        >
          <GppGoodOutlinedIcon sx={{ fontSize: 20, color: "var(--color-success)", mt: 0.2 }} />
          <Typography sx={{ fontSize: 12.5, color: "var(--text-secondary)" }}>
            <b>Tu trabajo guardado sigue ahí.</b> Ningún turno, cama ni nota se borra por esto.
          </Typography>
        </Box>
        <Link
          href="mailto:sebastian@smardx.com"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            fontSize: 13,
            color: "var(--color-primary-soft)",
          }}
        >
          <ChatBubbleOutlineOutlinedIcon sx={{ fontSize: 16 }} />
          Algo no cuadra, quiero escribirle a soporte
        </Link>
      </Box>

      {/* Columna derecha: cómo se destraba. #1680, opción C del mockup: el mismo
          bloque de planes + país + pago que el diálogo (`PlanesYPago`). En pago
          vencido en CLP suma, abajo, la alternativa de transferir (R32). */}
      <Box
        sx={{
          p: { xs: 3, sm: 5, md: 7 },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 2.75,
          backgroundColor: "rgba(var(--fg-rgb), 0.02)",
        }}
      >
        <Box data-testid="checkout-abrir" sx={{ width: "100%", maxWidth: 460 }}>
          <PlanesYPago
            titulo={esCobroProgramado ? "Tu plan" : esPagoVencido ? "Reactiva tu plan" : esPruebaMedico ? "O suscríbete a un plan" : "Elige tu plan"}
            bloqueado
            pagoVencido={esPagoVencido}
            modoAviso={esCobroProgramado}
            onOfreceSuelta={setOfreceSueltaReal}
            onFase={(fase, { botonPropio } = {}) => {
              setFaseCheckout(botonPropio && fase === "confirmando" ? "pendiente" : fase);
              if (fase === "confirmando") setCodeRetenido((previo) => previo || code);
            }}
            onListo={() => {
              setCodeRetenido(null);
              setCodeDeEvento(null);
            }}
            abajo={(estadoCheckout, ctx) =>
              ofreceTransferencia ? (
                <Box sx={{ mt: 2 }}>
                  <Button
                    data-testid="gate-transferencia-abrir"
                    onClick={() => setTransferenciaAbierta((v) => !v)}
                    variant="outlined"
                    sx={botonGhostSx}
                  >
                    Prefiero transferencia
                  </Button>
                  {transferenciaAbierta && (
                    <Box
                      data-testid="gate-transferencia"
                      sx={{
                        mt: 1.5,
                        p: "12px 14px",
                        borderRadius: "13px",
                        backgroundColor: "var(--bg-input)",
                      }}
                    >
                      <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1, color: "var(--text-primary)" }}>
                        Datos para transferir{" "}
                        {montoTransferencia(estadoCheckout, ctx) || (ctx?.planElegido ? "" : montoBruto)}
                      </Typography>
                      <Box sx={{ display: "grid", gap: 0.9 }}>
                        {DATOS_BANCARIOS.map((fila) => (
                          <FilaBanco key={fila.etiqueta} {...fila} />
                        ))}
                      </Box>
                      <Typography sx={{ fontSize: 11.5, color: "var(--text-faint)", mt: 1.25 }}>
                        Envía el comprobante a ese correo. Activamos tu plan apenas lo confirmamos.
                      </Typography>
                      {botonRevisar}
                    </Box>
                  )}
                </Box>
              ) : null
            }
          />
          {esCobroProgramado && (
            <Box data-testid="gate-cobro-acciones" sx={{ mt: 2, display: "grid", gap: 1 }}>
              {avisoPago?.puede_turno && (
                <Button
                  data-testid="gate-turno"
                  variant="outlined"
                  fullWidth
                  disabled={pidiendoHora}
                  onClick={async () => {
                    setPidiendoHora(true);
                    setErrorHora("");
                    try {
                      await pedirTurno();
                      await refreshUser();
                      setCodeDeEvento(null);
                    } catch {
                      setErrorHora("No pudimos abrirte el acceso. Escríbeme por WhatsApp y lo vemos.");
                    } finally {
                      setPidiendoHora(false);
                    }
                  }}
                  startIcon={<AccessTimeOutlinedIcon sx={{ fontSize: 18 }} />}
                  sx={botonGhostSx}
                >
                  {pidiendoHora ? "Abriendo..." : "Estoy de turno: sigue hasta las 9:00"}
                </Button>
              )}
              {avisoPago?.puede_turno && (
                <Typography sx={{ fontSize: 11.5, color: "var(--text-muted)", mt: -0.25 }}>
                  Una vez por mes. Lo que escribas se guarda igual.
                </Typography>
              )}
              {errorHora && (
                <Typography role="alert" sx={{ fontSize: 12.5, color: "var(--color-danger)" }}>
                  {errorHora}
                </Typography>
              )}
              {avisoPago?.whatsapp && <BotonWhatsApp sx={{ justifySelf: "start", ml: -1 }} />}
            </Box>
          )}
          {!ofreceTransferencia && !esCobroProgramado && faseCheckout !== "pendiente" && botonRevisar}
          {/* Hueco vacío de la prueba vencida (médicos e internos): #1883 monta aquí la entrada de su
              encuesta solo para internos, sin tocar el bloque de contacto de los médicos (#1809). */}
          {!esCobroProgramado && !esPagoVencido && <Box data-testid="suscripcion-gate-extra" />}
        </Box>
      </Box>
    </Box>
  );
};

export default SuscripcionGate;
